import {right} from "@popperjs/core";
import {AxiosError} from "axios";
import {CheckCircleSolid as AutoSelectedIcon, CheckCircleSolid as InheritedIcon} from "iconoir-react";
import {Dispatch, forwardRef, SetStateAction, useCallback, useEffect, useImperativeHandle, useState} from 'react'
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import Tooltip from "react-bootstrap/Tooltip";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {
    CertificateApi,
    CertificateSelectionApi,
    CertificateStore,
    CertificateValidationApi,
    type CertificateValidationResult,
    Expired,
    Match,
    MissingKeyAlias,
    type X509CertificateDetails
} from "../api/admin/generated";
import {Pager} from "../common/Pager";
import {RenderAlert} from "../common/RenderAlert";
import {RowsPerPage} from "../common/RowsPerPage";
import {SaveChangesRef} from "../common/SaveChangesRef";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {SettingsLevel} from "../settings/SettingsUtils";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";
import {CertificateFilter} from "./CertificateFilter";
import {CertificateRow} from "./CertificateRow";
import {CertificateTableHeader} from "./CertificateTableHeader";
import {SearchField} from "./SearchField";


interface EncryptionCertificatesProps {
    settingsLevel : SettingsLevel
    settingsLevelSubSelector : string
    setShouldSave: Dispatch<SetStateAction<boolean>>
    setSaving : Dispatch<SetStateAction<boolean>>
}

const certificateApi = new CertificateApi(ApiConfig.config());
const certificateValidationApi = new CertificateValidationApi(ApiConfig.config());
const certificateSelectionApi = new CertificateSelectionApi(ApiConfig.config());

const certificateStore : CertificateStore = CertificateStore.Certificates;

export const EncryptionCertificates = forwardRef<SaveChangesRef | undefined, EncryptionCertificatesProps> ((({
                settingsLevel,
                settingsLevelSubSelector,
                setShouldSave,
                setSaving} : EncryptionCertificatesProps, ref) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * All the filtered certificates
     */
    const [filteredCertificates, setFilteredCertificates] = useState<X509CertificateDetails[]>([]);
    /**
     * The total number of filtered certificates
     */
    const [filteredCertificateCount, setFilteredCertificateCount] = useState<number>(0);
    /**
     * All the auto-selected certificates
     */
    const [autoSelectedCertificates, setAutoSelectedCertificates] = useState<X509CertificateDetails[]>([]);
    /**
     * All the explicitly selected certificates
     */
    const [explictSelectedCertificates, setExplictSelectedCertificates] = useState<X509CertificateDetails[]>([]);
    /**
     * All the inherited certificates
     */
    const [inheritedCertificates, setInheritedCertificates] = useState<X509CertificateDetails[]>([]);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the current page
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of certificates to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(20);
    /**
     * The selected certificates
     */
    const [selectedCertificates, setSelectedCertificates] = useState<X509CertificateDetails[]>([]);
    /**
     * If true, all certificates that match the filter will be reloaded from the back-end
     */
    const [forceReloadFiltered, setForceReloadFiltered] = useState<boolean>(false);
    /**
     * The field to search on
     */
    const [searchField, setSearchField] = useState<SearchField>(SearchField.Email);
    /**
     * The field which is searched on
     */
    const [activeSearchValue, setActiveSearchValue] = useState<string | undefined>(settingsLevel === SettingsLevel.User ? settingsLevelSubSelector : "");
    /**
     * The field of the search box. This value will be copied to the activeSearchValue when apply is clicked
     */
    const [searchValue, setSearchValue] = useState<string | undefined>(activeSearchValue);
    /**
     * If true, all explict certificates will be reloaded from the back-end
     */
    const [forceReloadExplict, setForceReloadExplict] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    /**
     * Override the forwarded ref with the saveSettings method to allow the parent to save the modified settings
     */
    useImperativeHandle(ref, () => {
        return {
            saveChanges(){
                save();
            }
        }
    });

    /**
     * Load the number of certificates that match the active filter
     */
    useEffect(() => {
        logger.debug("Getting filtered certificate count");

        // reset state when we load new data
        setForceReloadFiltered(false);
        // Note: do not reset errorMessage because otherwise the message might get lost when re-rendering
        //       The error message can be closed by clicking the close icon

        let loadCertificateCountCallback;

        if ((activeSearchValue??"").length > 0)
        {
            switch (searchField)
            {
                case SearchField.Email:
                    loadCertificateCountCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesByEmailCount(
                            certificateStore, `%${activeSearchValue??""}%`,
                            Match.Like, Expired.UnexpiredOnly, MissingKeyAlias.Allowed);
                    };
                    break;
                case SearchField.Subject:
                    loadCertificateCountCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesBySubjectCount(
                            certificateStore, `%${activeSearchValue??""}%`,
                            Expired.UnexpiredOnly, MissingKeyAlias.Allowed);
                    };
                    break;
                case SearchField.Issuer:
                    loadCertificateCountCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesByIssuerCount(
                            certificateStore, `%${activeSearchValue??""}%`,
                            Expired.UnexpiredOnly, MissingKeyAlias.Allowed);
                    };
                    break;
            }
        }
        else {
            loadCertificateCountCallback = async () => {
                return await certificateApi.certificateControllerGetCertificatesCount(certificateStore,
                    Expired.UnexpiredOnly, MissingKeyAlias.Allowed);
            };
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        loadCertificateCountCallback()
            .then(response => {
                setFilteredCertificateCount(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting filtered certificates count", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        forceReloadFiltered,
        activeSearchValue,
        searchField,
        setSpinnerStateCounter,
        t]);

    /**
     * Load all certificates matching the filter
     */
    useEffect(() => {
        logger.debug("Getting filtered certificates");

        // reset state when we load new data
        setForceReloadFiltered(false);

        let loadCertificatesCallback;

        if ((activeSearchValue??"").length > 0)
        {
            switch (searchField)
            {
                case SearchField.Email:
                    loadCertificatesCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesByEmail(
                            certificateStore, `%${activeSearchValue??""}%`, Match.Like,
                            Expired.UnexpiredOnly, MissingKeyAlias.Allowed,
                            (currentPage - 1) * rowsPerPage, rowsPerPage);
                    };
                    break;
                case SearchField.Subject:
                    loadCertificatesCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesBySubject(
                            certificateStore, `%${activeSearchValue??""}%`,
                            Expired.UnexpiredOnly, MissingKeyAlias.Allowed,
                            (currentPage - 1) * rowsPerPage, rowsPerPage);
                    };
                    break;
                case SearchField.Issuer:
                    loadCertificatesCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesByIssuer(
                            certificateStore, `%${activeSearchValue??""}%`,
                            Expired.UnexpiredOnly, MissingKeyAlias.Allowed,
                            (currentPage - 1) * rowsPerPage, rowsPerPage);
                    };
                    break;
            }
        }
        else {
            loadCertificatesCallback = async () => {
                return await certificateApi.certificateControllerGetCertificates(certificateStore,
                    Expired.UnexpiredOnly, MissingKeyAlias.Allowed,
                    (currentPage - 1) * rowsPerPage, rowsPerPage);
            };
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        loadCertificatesCallback().then(response => {
            setFilteredCertificates(response.data);
        })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting filtered certificates", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        forceReloadFiltered,
        activeSearchValue,
        searchField,
        setSpinnerStateCounter,
        currentPage,
        rowsPerPage,
        t]);

    /**
     * Load all auto selected certificates
     */
    useEffect(() =>
    {
        // auto selected certificates are only available for users
        if (settingsLevel === SettingsLevel.User)
        {
            logger.debug("Getting auto selected certificates");

            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count + 1);
            }

            certificateSelectionApi.certificateSelectionControllerGetAutoSelectedEncryptionCertificatesForUser(
                settingsLevelSubSelector)
                .then(response => {
                    setAutoSelectedCertificates(response.data);
                })
                .catch((err: Error | AxiosError) => {
                    logger.error("Error getting filtered certificates", err);
                    Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                    setErrorMessage(Utils.getErrorMessage(err, t));
                })
                .finally(() => {
                    if (setSpinnerStateCounter !== undefined) {
                        setSpinnerStateCounter(count => count - 1);
                    }
                })
        }
    }, [navigate,
        setSpinnerStateCounter,
        settingsLevel,
        settingsLevelSubSelector,
        t
    ]);

    /**
     * Load all explicitly selected certificates
     */
    useEffect(() => {
        logger.debug("Getting explict certificates");

        let loadCertificatesCallback;

        // reset state
        setForceReloadExplict(false);

        switch (settingsLevel)
        {
            case SettingsLevel.User:
                loadCertificatesCallback = async () => {
                    return await certificateSelectionApi.certificateSelectionControllerGetExplicitCertificatesForUser(
                        settingsLevelSubSelector);
                };
                break;
            case SettingsLevel.Domain:
                loadCertificatesCallback = async () => {
                    return await certificateSelectionApi.certificateSelectionControllerGetExplicitCertificatesForDomain(
                        settingsLevelSubSelector);
                };
                break;
        }

        if (loadCertificatesCallback === undefined) {
            throw Error("loadCertificatesCallback is undefined");
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        loadCertificatesCallback().then(response =>
        {
            setExplictSelectedCertificates(response.data);

            // reset state when we load new data
            setSelectedCertificates(response.data);
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error getting explict certificates", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }, [navigate,
        setSpinnerStateCounter,
        settingsLevel,
        settingsLevelSubSelector,
        forceReloadExplict,
        t
    ]);

    /**
     * Load all inherited certificates
     */
    useEffect(() => {
        logger.debug("Getting inherited certificates");

        let loadCertificatesCallback;

        switch (settingsLevel)
        {
            case SettingsLevel.User:
                loadCertificatesCallback = async () => {
                    return await certificateSelectionApi.certificateSelectionControllerGetInheritedCertificatesForUser(
                        settingsLevelSubSelector);
                };
                break;
            case SettingsLevel.Domain:
                loadCertificatesCallback = async () => {
                    return await certificateSelectionApi.certificateSelectionControllerGetInheritedCertificatesForDomain(
                        settingsLevelSubSelector);
                };
                break;
        }

        if (loadCertificatesCallback === undefined) {
            throw Error("loadCertificatesCallback is undefined");
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        loadCertificatesCallback().then(response => {
            setInheritedCertificates(response.data);
        })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting inherited certificates", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        settingsLevel,
        settingsLevelSubSelector,
        t
    ]);

    /**
     * Saves changes to explict selected certificates
     */
    const save = () =>
    {
        logger.debug("saving selected certificates", selectedCertificates)

        let saveCertificatesCallback;

        switch (settingsLevel)
        {
            case SettingsLevel.User:
                saveCertificatesCallback = async () => {
                    return await certificateSelectionApi.certificateSelectionControllerSetExplicitCertificatesForUser(
                        settingsLevelSubSelector,
                        selectedCertificates.map(cert => cert.thumbprint));
                };
                break;
            case SettingsLevel.Domain:
                saveCertificatesCallback = async () => {
                    return await certificateSelectionApi.certificateSelectionControllerSetExplicitCertificatesForDomain(
                        settingsLevelSubSelector,
                        selectedCertificates.map(cert => cert.thumbprint));
                };
                break;
        }

        if (saveCertificatesCallback === undefined) {
            throw Error("saveCertificatesCallback is undefined");
        }

        // tell the parent that this component is saving (to show a spinner)
        setSaving(true);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        saveCertificatesCallback().then(() => {
            setShouldSave(false);
            // force a reload of the explict certificates
            setForceReloadExplict(true);
        })
            .catch((err: Error | AxiosError) => {
                logger.error("Error setting explict certificates", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() =>
            {
                setSaving(false);

                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }

    /**
     * Called when a certificate is selected (i.e., when the checkbox is checked)
     */
    const onSelectCertificate = (checked : boolean, certificate : X509CertificateDetails) =>
    {
        // we need to create a local array to get instant updated data because an update state value is only available
        // at the next render
        let updatedSelectedCertificates

        if (checked) {
            // add to selected
            updatedSelectedCertificates = [...selectedCertificates, certificate];
        }
        else {
            // remove from selected
            updatedSelectedCertificates = selectedCertificates.filter(e => e !== certificate);
        }

        setSelectedCertificates(updatedSelectedCertificates);

        // if the selected certificates contain the same certificates as the explicit certificates, saving is not
        // needed
        setShouldSave(explictSelectedCertificates.length != updatedSelectedCertificates.length ||
                        !explictSelectedCertificates.every(cert1 =>
                            updatedSelectedCertificates.some(cert2 => cert1.thumbprint === cert2.thumbprint)))
    }

    /**
     * Checks the validity of a certificate for encryption using the certificate validation API.
     *
     * NOTE: this must be wrapped in a useCallback call to prevent unlimited rendering because the spinner state is set
     */
    const checkCertificateValidity = (useCallback((certificate : X509CertificateDetails, setCertificateValidationResult : Dispatch<SetStateAction<CertificateValidationResult | undefined>>) =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        certificateValidationApi.certificateValidationControllerValidateCertificateForEncryption(certificateStore, certificate.thumbprint)
            .then((response) =>
            {
                const validationResult : CertificateValidationResult = response.data;

                setCertificateValidationResult(validationResult);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error certificate validation result", err);
                setCertificateValidationResult(undefined);
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }, [setSpinnerStateCounter]));

    function getCertificates(): X509CertificateDetails[]
    {
        const certificates : X509CertificateDetails[] = [...autoSelectedCertificates];

        // add inherited certificates but not if the certificate is already added
        certificates.push(...inheritedCertificates.filter(cert1 =>
            !certificates.some(cert2 => cert2.thumbprint === cert1.thumbprint)));

        // add explict certificates but not if the certificate is already added
        certificates.push(...explictSelectedCertificates.filter(cert1 =>
            !certificates.some(cert2 => cert2.thumbprint === cert1.thumbprint)));

        // add all filtered certificates but not if the certificate is already added
        certificates.push(...filteredCertificates.filter(cert1 =>
            !certificates.some(cert2 => cert2.thumbprint === cert1.thumbprint)));

        return certificates;
    }

    /**
     * Selection checkbox is disabled if the certificate is an auto selected certificate or inherited
     * @param certificate
     */
    function isCheckboxDisabled(certificate : X509CertificateDetails)
    {
        return autoSelectedCertificates.some(cert => cert.thumbprint === certificate.thumbprint) ||
               inheritedCertificates.some(cert => cert.thumbprint === certificate.thumbprint);
    }

    return (
        <>
            <Row>
                <Col>
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                </Col>
            </Row>

            <Row>
                <div className="d-flex flex-row align-items-center mb-4">
                    <div className="d-flex flex-fill justify-content-end align-items-center">
                        <div className="d-xl-flex">
                            <CertificateFilter searchField={searchField}
                                               setSearchField={setSearchField}
                                               searchValue={searchValue}
                                               setSearchValue={setSearchValue}
                                               activeSearchValue={activeSearchValue}
                                               setActiveSearchValue={setActiveSearchValue}
                            />
                        </div>
                    </div>
                </div>
            </Row>

            <Row className="h-100 w-100 overflow-auto">
                <Col>
                    <Table className="sticky sticky-x" bordered hover>
                        <CertificateTableHeader/>
                        <tbody>
                        {getCertificates().map((certificate) => (
                            <CertificateRow key={certificate.thumbprint} certificate={certificate} checkCertificateValidityCallback={checkCertificateValidity}>
                                {/* Add selection checkbox to first column */}
                                <Form.Check
                                    type="checkbox"
                                    className="me-0"
                                    checked={selectedCertificates.includes(certificate)}
                                    onChange={(e) => onSelectCertificate(e.target.checked, certificate)}
                                    disabled={isCheckboxDisabled(certificate)}
                                    inline
                                />

                                {autoSelectedCertificates.some(cert => cert.thumbprint === certificate.thumbprint) &&
                                    <OverlayTrigger
                                        placement={right}
                                        overlay={
                                            <Tooltip>
                                                {t("certificates.certificate-auto-selected")}
                                            </Tooltip>
                                        }
                                    >
                                        <AutoSelectedIcon className="text-success table-icon-width table-icon-height ms-2"/>
                                    </OverlayTrigger>
                                }

                                {inheritedCertificates.some(cert => cert.thumbprint === certificate.thumbprint) &&
                                    <OverlayTrigger
                                        placement={right}
                                        overlay={
                                            <Tooltip>
                                                {t("certificates.certificate-inherited")}
                                            </Tooltip>
                                        }
                                    >
                                        <InheritedIcon className="text-warning table-icon-width table-icon-height ms-2"/>
                                    </OverlayTrigger>
                                }
                            </CertificateRow>
                        ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>

            <Row className="mt-4 align-items-baseline">
                <div className="col-6 d-flex">
                    <div className="d-flex justify-content-start align-items-center d-none d-md-inline-flex me-4">
                        {filteredCertificateCount} {t("common.results")}
                    </div>

                    <div className="d-flex justify-content-start align-items-center">
                        <RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage}
                                     setCurrentPage={setCurrentPage}/>
                    </div>
                </div>
                <div className="col-6">
                    <div className="d-flex justify-content-end">
                        <Pager itemCount={filteredCertificateCount}
                               currentPage={currentPage}
                               setCurrentPage={setCurrentPage}
                               maxItemsPerPage={rowsPerPage}
                        />
                    </div>
                </div>
            </Row>
        </>
    );
}))

// see https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/display-name.md why this is needed
EncryptionCertificates.displayName="EncryptionCertificates"
